/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Placement } from 'popper.js'
import type { FC } from 'react'
import React, { useMemo } from 'react'

import type { TooltipProps } from './types'
import type { Theme, ThemeUICSSObject } from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import { get } from '../../utils/object'
import { omitSpace } from '../../utils/props-utils'
import { PanePrivate } from '../_layouts/pane/pane'
import type { PaneProps } from '../_layouts/pane/types'
import { BoxPrivate } from '../_primitives/box/box'

export const tooltipPlacement = {
  /* Styles applied to the tooltip (label wrapper) element if `placement` contains "left". */
  left: {
    transformOrigin: 'right center',
    my: 0,
    mx: 'xxs'
  },
  /* Styles applied to the tooltip (label wrapper) element if `placement` contains "right". */
  right: {
    transformOrigin: 'left center',
    my: 0,
    mx: 'xxs'
  },
  /* Styles applied to the tooltip (label wrapper) element if `placement` contains "top". */
  top: {
    transformOrigin: 'center bottom',
    my: 'xxs',
    mx: 0
  },
  /* Styles applied to the tooltip (label wrapper) element if `placement` contains "bottom". */
  bottom: {
    transformOrigin: 'center top',
    my: 'xxs',
    mx: 0
  }
}

type PopperContentProps = Omit<PaneProps, 'title'> & {
  theme?: Theme
  title?: TooltipProps['title']
  Component: React.ComponentType<any>
  TransitionProps: TooltipProps['TransitionProps']
  arrow?: boolean
  placement: Placement
  ComponentTransitionProps?: {
    in: boolean
    onEnter: () => {}
    onExited: () => {}
  }
  ignoreNonTouchEvents: React.MutableRefObject<boolean>
  setArrowRef: React.Dispatch<any>
}

const arrowBaseStyle: ThemeUICSSObject = {
  position: 'absolute',
  fontSize: '0.25em',
  color: 'gray',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: 0,
    height: 0,
    borderStyle: 'solid'
  }
}

const TooltipContent: FC<PopperContentProps> = props => {
  const theme = useSafeThemeUI(props)
  const {
    variant,
    placement,
    border,
    elevation,
    Component,
    ComponentTransitionProps,
    TransitionProps,
    title,
    arrow,
    ignoreNonTouchEvents,
    setArrowRef,
    ...rest
  } = props

  const baseTooltipPaneStyle = useMemo(
    () => ({
      /* Styles applied to the tooltip (label wrapper) element if the tooltip is opened by touch. */
      ...(ignoreNonTouchEvents.current
        ? {
            px: 'md',
            py: 'sm',
            fontSize: 'sm'
          }
        : {}),
      /* Styles applied to the tooltip (label wrapper) element if `arrow={true}`. */
      ...(arrow
        ? {
            position: 'relative',
            margin: '0'
          }
        : {}),
      ...(tooltipPlacement as any)[placement.split('-')[0]]
    }),
    [arrow, ignoreNonTouchEvents, placement]
  )
  return (
    <Component
      timeout={get(theme, 'transitions.duration.shortest', 150)}
      {...ComponentTransitionProps}
      {...TransitionProps}
    >
      <PanePrivate
        variant={variant}
        __themeKey="tooltips"
        styledBorder={border}
        elevation={elevation}
        __css={baseTooltipPaneStyle}
        {...omitSpace(rest)}
      >
        {title}
        {arrow ? (
          <BoxPrivate
            as="span"
            ref={setArrowRef}
            className="csg-tui-tooltip-arrow"
            __css={arrowBaseStyle}
          />
        ) : null}
      </PanePrivate>
    </Component>
  )
}

export default TooltipContent
